import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  FlagOutlined as KraIcon,
  CheckCircleOutline as KpiIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

const CYCLES = ['Q1', 'Q2', 'Q3', 'Q4', 'Annual'];

const cycleColors = {
  Q1: 'primary',
  Q2: 'secondary',
  Q3: 'warning',
  Q4: 'error',
  Annual: 'success',
};

const statusColors = {
  draft: 'default',
  submitted: 'warning',
  approved: 'success',
  rejected: 'error',
};

const emptyForm = () => ({
  reviewCycle: 'Q1',
  reviewYear: new Date().getFullYear(),
  kras: [],
});

const KpiProposalTab = () => {
  const { getAuthHeaders } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [existingKras, setExistingKras] = useState([]); // [{ name, description }] from the employee's current cycle
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'form'
  const [editingProposalId, setEditingProposalId] = useState(null);
  const [formData, setFormData] = useState(emptyForm());

  const fetchProposals = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/performance/proposals/mine`, {
        headers: getAuthHeaders(),
      });
      setProposals(response.data.proposals || []);
    } catch (error) {
      console.error('Error fetching KPI proposals:', error);
      toast.error('Failed to load your KPI/KRA proposals');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingKras = async () => {
    try {
      const response = await axios.get(`${API_URL}/performance/my-forms`, {
        headers: getAuthHeaders(),
      });
      const seen = new Map();
      (response.data.forms || []).forEach(({ form }) => {
        (form?.kras || []).forEach((kra) => {
          if (kra?.name && !seen.has(kra.name)) {
            seen.set(kra.name, kra.description || '');
          }
        });
      });
      setExistingKras(Array.from(seen.entries()).map(([name, description]) => ({ name, description })));
    } catch (error) {
      // Non-fatal: this only powers the "reuse an existing KRA" convenience
      console.error('Error fetching existing KRAs:', error);
    }
  };

  useEffect(() => {
    fetchProposals();
    fetchExistingKras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNewProposal = () => {
    setEditingProposalId(null);
    setFormData(emptyForm());
    setViewMode('form');
  };

  const handleEditProposal = (proposal) => {
    setEditingProposalId(proposal._id);
    setFormData({
      reviewCycle: proposal.reviewCycle,
      reviewYear: proposal.reviewYear,
      kras: proposal.kras.map((kra) => ({
        name: kra.name || '',
        description: kra.description || '',
        weightage: kra.weightage || 0,
        kpis: (kra.kpis || []).map((kpi) => ({
          name: kpi.name || '',
          description: kpi.description || '',
        })),
      })),
    });
    setViewMode('form');
  };

  const handleAddKRA = (prefillName = '') => {
    setFormData((prev) => ({
      ...prev,
      kras: [...prev.kras, { name: prefillName, description: '', weightage: 0, kpis: [{ name: '', description: '' }] }],
    }));
  };

  const handleUpdateKRA = (kraIndex, field, value) => {
    setFormData((prev) => {
      const kras = [...prev.kras];
      kras[kraIndex] = { ...kras[kraIndex], [field]: value };
      return { ...prev, kras };
    });
  };

  const handleDeleteKRA = (kraIndex) => {
    setFormData((prev) => ({
      ...prev,
      kras: prev.kras.filter((_, i) => i !== kraIndex),
    }));
  };

  const handleAddKPI = (kraIndex) => {
    setFormData((prev) => {
      const kras = [...prev.kras];
      kras[kraIndex] = {
        ...kras[kraIndex],
        kpis: [...kras[kraIndex].kpis, { name: '', description: '' }],
      };
      return { ...prev, kras };
    });
  };

  const handleUpdateKPI = (kraIndex, kpiIndex, value) => {
    setFormData((prev) => {
      const kras = [...prev.kras];
      const kpis = [...kras[kraIndex].kpis];
      kpis[kpiIndex] = { ...kpis[kpiIndex], name: value };
      kras[kraIndex] = { ...kras[kraIndex], kpis };
      return { ...prev, kras };
    });
  };

  const handleDeleteKPI = (kraIndex, kpiIndex) => {
    setFormData((prev) => {
      const kras = [...prev.kras];
      kras[kraIndex] = {
        ...kras[kraIndex],
        kpis: kras[kraIndex].kpis.filter((_, i) => i !== kpiIndex),
      };
      return { ...prev, kras };
    });
  };

  const saveDraft = async () => {
    try {
      setSaving(true);
      const response = await axios.post(
        `${API_URL}/performance/proposals/draft`,
        formData,
        { headers: getAuthHeaders() }
      );
      return response.data.proposal;
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(error.response?.data?.message || 'Failed to save draft');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    const proposal = await saveDraft();
    if (proposal) {
      toast.success('Draft saved');
      setEditingProposalId(proposal._id);
      fetchProposals();
    }
  };

  const handleSubmitForApproval = async () => {
    setSubmitting(true);
    const proposal = await saveDraft();
    if (!proposal) {
      setSubmitting(false);
      return;
    }
    try {
      await axios.post(
        `${API_URL}/performance/proposals/${proposal._id}/submit`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Proposal submitted for manager approval');
      setViewMode('list');
      fetchProposals();
    } catch (error) {
      console.error('Error submitting proposal:', error);
      toast.error(error.response?.data?.message || 'Failed to submit proposal');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Propose KPIs / KRAs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Jot down goals for an upcoming review cycle for your manager to approve.
          </Typography>
        </Box>
        <Box>
          <Button
            variant={viewMode === 'form' ? 'contained' : 'outlined'}
            onClick={handleNewProposal}
            sx={{ mr: 1 }}
            startIcon={<AddIcon />}
          >
            New Proposal
          </Button>
          <Button
            variant={viewMode === 'list' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('list')}
          >
            My Proposals ({proposals.length})
          </Button>
        </Box>
      </Box>

      {viewMode === 'form' ? (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Review Cycle</InputLabel>
                <Select
                  label="Review Cycle"
                  value={formData.reviewCycle}
                  disabled={!!editingProposalId}
                  onChange={(e) => setFormData((prev) => ({ ...prev, reviewCycle: e.target.value }))}
                >
                  {CYCLES.map((cycle) => (
                    <MenuItem key={cycle} value={cycle}>{cycle}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Review Year"
                value={formData.reviewYear}
                disabled={!!editingProposalId}
                onChange={(e) => setFormData((prev) => ({ ...prev, reviewYear: parseInt(e.target.value, 10) || '' }))}
              />
            </Grid>
          </Grid>

          {existingKras.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Just adding a goal to something you already do? Tap a KRA to reuse it:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {existingKras.map((kra) => (
                  <Chip
                    key={kra.name}
                    label={kra.name}
                    variant="outlined"
                    clickable
                    onClick={() => handleAddKRA(kra.name)}
                  />
                ))}
              </Box>
            </Box>
          )}

          {formData.kras.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No KRAs added yet. Tap a KRA above to reuse it, or add a new one below.
            </Alert>
          )}

          <Stack spacing={2} sx={{ mb: 2 }}>
            {formData.kras.map((kra, kraIndex) => (
              <Paper key={kraIndex} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Autocomplete
                    freeSolo
                    fullWidth
                    size="small"
                    options={existingKras.map((k) => k.name)}
                    inputValue={kra.name}
                    onInputChange={(e, newValue) => handleUpdateKRA(kraIndex, 'name', newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label={`KRA ${kraIndex + 1}`} placeholder="e.g. Client Communication" required />
                    )}
                  />
                  <IconButton color="error" onClick={() => handleDeleteKRA(kraIndex)} title="Remove this KRA">
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    KPIs / Goals
                  </Typography>
                  <Stack spacing={1}>
                    {kra.kpis.map((kpi, kpiIndex) => (
                      <Box key={kpiIndex} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          value={kpi.name}
                          onChange={(e) => handleUpdateKPI(kraIndex, kpiIndex, e.target.value)}
                          placeholder="e.g. Respond to client emails within 24 hours"
                          required
                        />
                        <IconButton size="small" color="error" onClick={() => handleDeleteKPI(kraIndex, kpiIndex)} title="Remove this KPI">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => handleAddKPI(kraIndex)} sx={{ mt: 1 }}>
                    Add KPI
                  </Button>
                </Box>
              </Paper>
            ))}
          </Stack>

          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleAddKRA()}>
            Add a New KRA
          </Button>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="outlined" onClick={() => setViewMode('list')}>
              Cancel
            </Button>
            <Button variant="outlined" onClick={handleSaveDraft} disabled={saving || submitting}>
              {saving ? <CircularProgress size={20} /> : 'Save Draft'}
            </Button>
            <Button variant="contained" onClick={handleSubmitForApproval} disabled={saving || submitting}>
              {submitting ? <CircularProgress size={20} /> : 'Submit for Approval'}
            </Button>
          </Box>
        </Paper>
      ) : (
        <Box>
          {proposals.length === 0 ? (
            <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                You haven't proposed any KPIs/KRAs yet. Click "New Proposal" to get started.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={2}>
              {proposals.map((proposal) => {
                const isEditable = ['draft', 'rejected'].includes(proposal.status);
                return (
                  <Card key={proposal._id} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={`${proposal.reviewCycle} ${proposal.reviewYear}`}
                          color={cycleColors[proposal.reviewCycle] || 'default'}
                          size="small"
                        />
                        <Chip
                          label={proposal.status}
                          color={statusColors[proposal.status] || 'default'}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                        <Box sx={{ flexGrow: 1 }} />
                        {isEditable && (
                          <Button size="small" variant="outlined" onClick={() => handleEditProposal(proposal)}>
                            Edit
                          </Button>
                        )}
                      </Box>

                      {proposal.status === 'rejected' && proposal.rejectionReason && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          <strong>Manager's feedback:</strong> {proposal.rejectionReason}
                        </Alert>
                      )}

                      <Divider sx={{ mb: 1.5 }} />

                      {(proposal.kras || []).length > 0 ? (
                        proposal.kras.map((kra, kraIdx) => (
                          <Accordion key={kraIdx} disableGutters elevation={0}
                            sx={{ border: '1px solid', borderColor: 'divider', mb: 1, borderRadius: 1, '&:before': { display: 'none' } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
                                <KraIcon color="primary" fontSize="small" />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                  {kra.name}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                              {kra.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                  {kra.description}
                                </Typography>
                              )}
                              <Stack spacing={1}>
                                {(kra.kpis || []).map((kpi, kpiIdx) => (
                                  <Box key={kpiIdx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <KpiIcon sx={{ color: 'success.main', mt: 0.2 }} fontSize="small" />
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{kpi.name}</Typography>
                                      {kpi.description && (
                                        <Typography variant="body2" color="text.secondary">{kpi.description}</Typography>
                                      )}
                                    </Box>
                                  </Box>
                                ))}
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">No KRAs added yet.</Typography>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};

export default KpiProposalTab;
